import React, { useState, useEffect } from 'react';
import { 
  Plus, 
  Search, 
  Filter, 
  MoreVertical, 
  Edit, 
  Trash2, 
  Key, 
  RefreshCw, 
  Eye, 
  EyeOff,
  Download,
  AlertCircle,
  CheckCircle,
  Clock,
  Activity,
  MapPin,
  Building,
  Server,
  Shield,
  Wifi,
  WifiOff
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import apiClient from '../../../services/apiClient';

const ECGWorkstationManagement = () => {
  const [workstations, setWorkstations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [healthFilter, setHealthFilter] = useState('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedWorkstation, setSelectedWorkstation] = useState(null);
  const [stats, setStats] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [perPage] = useState(10);

  // Form states
  const [formData, setFormData] = useState({
    workstation_name: '',
    email: '',
    location: '',
    department: '',
    workstation_version: '3.4.7',
    supported_report_types: ['ECG-S', 'ECG-M'],
    processing_time_minutes: 10,
    max_concurrent_requests: 5,
    notes: ''
  });

  useEffect(() => {
    fetchWorkstations();
    fetchStats();
  }, [currentPage, searchTerm, statusFilter, healthFilter]);

  const fetchWorkstations = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: currentPage,
        per_page: perPage,
        ...(searchTerm && { search: searchTerm }),
        ...(statusFilter !== 'all' && { status: statusFilter }),
        ...(healthFilter !== 'all' && { health_status: healthFilter })
      });

      const response = await apiClient.get(`/ecg-workstation-management/workstations?${params}`);
      setWorkstations(response.data.workstations);
      setTotalPages(response.data.total_pages);
    } catch (error) {
      console.error('Error fetching workstations:', error);
      toast.error('Failed to fetch workstations');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await apiClient.get('/ecg-workstation-management/workstations/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const handleCreateWorkstation = async (e) => {
    e.preventDefault();
    try {
      await apiClient.post('/ecg-workstation-management/workstations', formData);
      toast.success('Workstation created successfully');
      setShowCreateModal(false);
      resetForm();
      fetchWorkstations();
      fetchStats();
    } catch (error) {
      console.error('Error creating workstation:', error);
      toast.error(error.response?.data?.detail || 'Failed to create workstation');
    }
  };

  const handleUpdateWorkstation = async (e) => {
    e.preventDefault();
    try {
      await apiClient.put(`/ecg-workstation-management/workstations/${selectedWorkstation.id}`, formData);
      toast.success('Workstation updated successfully');
      setShowEditModal(false);
      setSelectedWorkstation(null);
      resetForm();
      fetchWorkstations();
    } catch (error) {
      console.error('Error updating workstation:', error);
      toast.error(error.response?.data?.detail || 'Failed to update workstation');
    }
  };

  const handleDeleteWorkstation = async (workstationId) => {
    if (!window.confirm('Are you sure you want to delete this workstation?')) return;
    
    try {
      await apiClient.delete(`/ecg-workstation-management/workstations/${workstationId}`);
      toast.success('Workstation deleted successfully');
      fetchWorkstations();
      fetchStats();
    } catch (error) {
      console.error('Error deleting workstation:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete workstation');
    }
  };

  const handleResetPassword = async (workstationId) => {
    const newPassword = prompt('Enter new password (minimum 8 characters):');
    if (!newPassword || newPassword.length < 8) {
      toast.error('Password must be at least 8 characters long');
      return;
    }

    try {
      await apiClient.post(`/ecg-workstation-management/workstations/${workstationId}/reset-password`, {
        workstation_code: workstations.find(w => w.id === workstationId)?.workstation_code,
        new_password: newPassword
      });
      toast.success('Password reset successfully');
    } catch (error) {
      console.error('Error resetting password:', error);
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    }
  };

  const resetForm = () => {
    setFormData({
      workstation_name: '',
      email: '',
      location: '',
      department: '',
      workstation_version: '3.4.7',
      supported_report_types: ['ECG-S', 'ECG-M'],
      processing_time_minutes: 10,
      max_concurrent_requests: 5,
      notes: ''
    });
  };

  const openEditModal = (workstation) => {
    setSelectedWorkstation(workstation);
    setFormData({
      workstation_name: workstation.workstation_name,
      email: workstation.email,
      location: workstation.location || '',
      department: workstation.department || '',
      workstation_version: workstation.workstation_version,
      supported_report_types: workstation.supported_report_types || ['ECG-S', 'ECG-M'],
      processing_time_minutes: workstation.processing_time_minutes,
      max_concurrent_requests: workstation.max_concurrent_requests,
      notes: workstation.notes || ''
    });
    setShowEditModal(true);
  };

  const getStatusIcon = (workstation) => {
    if (!workstation.is_active) return <WifiOff className="w-4 h-4 text-gray-400" />;
    if (workstation.health_status === 'healthy') return <Wifi className="w-4 h-4 text-green-500" />;
    if (workstation.health_status === 'unhealthy') return <AlertCircle className="w-4 h-4 text-red-500" />;
    return <Clock className="w-4 h-4 text-yellow-500" />;
  };

  const getStatusColor = (workstation) => {
    if (!workstation.is_active) return 'bg-gray-100 text-gray-600';
    if (workstation.health_status === 'healthy') return 'bg-green-100 text-green-700';
    if (workstation.health_status === 'unhealthy') return 'bg-red-100 text-red-700';
    return 'bg-yellow-100 text-yellow-700';
  };

  const getAuthStatus = (workstation) => {
    if (workstation.is_authenticated) {
      return <CheckCircle className="w-4 h-4 text-green-500" />;
    }
    return <AlertCircle className="w-4 h-4 text-red-500" />;
  };

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">ECG Workstation Management</h1>
            <p className="text-gray-600 mt-2">Manage and monitor ECG workstations across your organization</p>
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
          >
            <Plus className="w-5 h-5" />
            Add Workstation
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white p-6 rounded-lg shadow-sm border">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Workstations</p>
                <p className="text-2xl font-bold text-gray-900">{stats.total_workstations}</p>
              </div>
              <Server className="w-8 h-8 text-blue-500" />
            </div>
          </div>
          
          <div className="bg-white p-6 rounded-lg shadow-sm border">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Active Workstations</p>
                <p className="text-2xl font-bold text-green-600">{stats.active_workstations}</p>
              </div>
              <Activity className="w-8 h-8 text-green-500" />
            </div>
          </div>
          
          <div className="bg-white p-6 rounded-lg shadow-sm border">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Healthy Workstations</p>
                <p className="text-2xl font-bold text-green-600">{stats.healthy_workstations}</p>
              </div>
              <CheckCircle className="w-8 h-8 text-green-500" />
            </div>
          </div>
          
          <div className="bg-white p-6 rounded-lg shadow-sm border">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Authenticated</p>
                <p className="text-2xl font-bold text-blue-600">{stats.authenticated_workstations}</p>
              </div>
              <Shield className="w-8 h-8 text-blue-500" />
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white p-6 rounded-lg shadow-sm border mb-6">
        <div className="flex flex-wrap gap-4">
          <div className="flex-1 min-w-64">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search workstations..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>
          
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
          
          <select
            value={healthFilter}
            onChange={(e) => setHealthFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="all">All Health</option>
            <option value="healthy">Healthy</option>
            <option value="unhealthy">Unhealthy</option>
            <option value="unknown">Unknown</option>
          </select>
          
          <button
            onClick={fetchWorkstations}
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors flex items-center gap-2"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
        </div>
      </div>

      {/* Workstations Table */}
      <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Workstation
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Location
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Requests
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Last Activity
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan="6" className="px-6 py-12 text-center">
                    <div className="flex items-center justify-center">
                      <RefreshCw className="w-6 h-6 animate-spin text-blue-500 mr-2" />
                      Loading workstations...
                    </div>
                  </td>
                </tr>
              ) : workstations.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-6 py-12 text-center text-gray-500">
                    No workstations found
                  </td>
                </tr>
              ) : (
                workstations.map((workstation) => (
                  <tr key={workstation.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0">
                          {getStatusIcon(workstation)}
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">
                            {workstation.workstation_name}
                          </div>
                          <div className="text-sm text-gray-500">
                            {workstation.workstation_code}
                          </div>
                          <div className="text-xs text-gray-400">
                            {workstation.email}
                          </div>
                        </div>
                      </div>
                    </td>
                    
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center space-x-2">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(workstation)}`}>
                          {workstation.is_active ? 'Active' : 'Inactive'}
                        </span>
                        {getAuthStatus(workstation)}
                      </div>
                      <div className="text-xs text-gray-500 mt-1">
                        Health: {workstation.health_status}
                      </div>
                    </td>
                    
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center text-sm text-gray-900">
                        <MapPin className="w-4 h-4 text-gray-400 mr-1" />
                        {workstation.location || 'Not specified'}
                      </div>
                      <div className="text-xs text-gray-500">
                        <Building className="w-3 h-3 inline mr-1" />
                        {workstation.department || 'General'}
                      </div>
                    </td>
                    
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <div className="flex items-center">
                        <Activity className="w-4 h-4 text-gray-400 mr-1" />
                        {workstation.current_requests} / {workstation.max_concurrent_requests}
                      </div>
                      <div className="text-xs text-gray-500">
                        {workstation.processing_time_minutes}min avg
                      </div>
                    </td>
                    
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {workstation.last_activity ? (
                        <div>
                          {new Date(workstation.last_activity).toLocaleDateString()}
                          <div className="text-xs">
                            {new Date(workstation.last_activity).toLocaleTimeString()}
                          </div>
                        </div>
                      ) : (
                        'Never'
                      )}
                    </td>
                    
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => openEditModal(workstation)}
                          className="text-blue-600 hover:text-blue-900"
                          title="Edit"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        
                        <button
                          onClick={() => handleResetPassword(workstation.id)}
                          className="text-yellow-600 hover:text-yellow-900"
                          title="Reset Password"
                        >
                          <Key className="w-4 h-4" />
                        </button>
                        
                        <button
                          onClick={() => handleDeleteWorkstation(workstation.id)}
                          className="text-red-600 hover:text-red-900"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        
        {/* Pagination */}
        {totalPages > 1 && (
          <div className="bg-white px-4 py-3 border-t border-gray-200 sm:px-6">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-700">
                Page {currentPage} of {totalPages}
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-1 text-sm border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                >
                  Previous
                </button>
                <button
                  onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                  className="px-3 py-1 text-sm border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Create Workstation Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold mb-4">Create New Workstation</h2>
            <form onSubmit={handleCreateWorkstation}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Workstation Name *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.workstation_name}
                    onChange={(e) => setFormData({...formData, workstation_name: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="e.g., Main Cardiology Lab"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="cardiology@hospital.com"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Location
                  </label>
                  <input
                    type="text"
                    value={formData.location}
                    onChange={(e) => setFormData({...formData, location: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Central Medical Center - Floor 2"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Department
                  </label>
                  <input
                    type="text"
                    value={formData.department}
                    onChange={(e) => setFormData({...formData, department: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Cardiology"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Processing Time (minutes)
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={formData.processing_time_minutes}
                    onChange={(e) => setFormData({...formData, processing_time_minutes: parseInt(e.target.value)})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Max Concurrent Requests
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={formData.max_concurrent_requests}
                    onChange={(e) => setFormData({...formData, max_concurrent_requests: parseInt(e.target.value)})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Notes
                </label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  rows="3"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Additional notes about this workstation..."
                />
              </div>
              
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateModal(false);
                    resetForm();
                  }}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                >
                  Create Workstation
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Workstation Modal */}
      {showEditModal && selectedWorkstation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold mb-4">Edit Workstation</h2>
            <form onSubmit={handleUpdateWorkstation}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Workstation Name *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.workstation_name}
                    onChange={(e) => setFormData({...formData, workstation_name: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Location
                  </label>
                  <input
                    type="text"
                    value={formData.location}
                    onChange={(e) => setFormData({...formData, location: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Department
                  </label>
                  <input
                    type="text"
                    value={formData.department}
                    onChange={(e) => setFormData({...formData, department: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Processing Time (minutes)
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={formData.processing_time_minutes}
                    onChange={(e) => setFormData({...formData, processing_time_minutes: parseInt(e.target.value)})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Max Concurrent Requests
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={formData.max_concurrent_requests}
                    onChange={(e) => setFormData({...formData, max_concurrent_requests: parseInt(e.target.value)})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Notes
                </label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  rows="3"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowEditModal(false);
                    setSelectedWorkstation(null);
                    resetForm();
                  }}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                >
                  Update Workstation
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ECGWorkstationManagement;
